import { IInviteOverview, IMemberOverview, IUserOverview } from '../../../../lib/types';
import React, { PropsWithChildren, useMemo } from 'react';
import { RIDialog } from '../../../_core/dialog/dialog.component';
import { SendInvitesContainer } from '../../../domains/invite/send/send-invites-form.container';
import { TableWithActions } from '../../../_core/table/table-with-actions.component';
import { IAdminProps } from '../../../_core/core.types';
import { ITableHeaderCell, ITableOptionsConfig, ITableRowValues } from '../../../_core/table/table.types';
import { sortMethodTuple, useTableSortConfig } from '../../../_core/table/utils/table-sort';
import { activeStatus, inviteStatus } from '../../../../utils/wording.helpers';
import { nameableListToString } from '../../../_core/table/utils/table-utils';
import { rolesToString } from './staff-table.utils';
import { getName } from '../../../../utils/name.utils';

interface IStaffTableProps
  extends PropsWithChildren,
    IAdminProps,
    ITableOptionsConfig<IInviteOverview | IMemberOverview> {
  inviteStaff: boolean;
  loading: boolean;
  members?: IMemberOverview[];
  onSelect: (_index: number, _selected?: boolean) => void;
  onSelectAll: (_selected: boolean) => void;
  selected?: boolean[];
  setInviteStaff: React.Dispatch<React.SetStateAction<boolean>>;
  staff: IStaffTableRowValues[];
}

export interface IStaffTableRowValues extends ITableRowValues {
  invite?: IInviteOverview;
  member?: IMemberOverview;
}

export const StaffTable: React.FC<IStaffTableProps> = ({
  children,
  inviteStaff,
  loading,
  members,
  setInviteStaff,
  staff,
  ...rest
}) => {
  const headers: ITableHeaderCell[] = [
    { content: 'Name', sortKey: 'user' },
    { content: 'Email' },
    { content: 'Roles', sortKey: 'roles' },
    { content: 'Status', sortKey: 'deletedAt' },
    { content: 'Departments', sortKey: 'department' },
    { content: 'Locations', sortKey: 'location' },
    { content: '' }
  ];

  const { sortFunc, ...restSort } = useTableSortConfig({
    baseValueExtractor: (r: IStaffTableRowValues) => r.invite ?? r.member,
    sortMethods: {
      deletedAt: sortMethodTuple('string', (deletedAt?: Date) => activeStatus(!deletedAt)),
      user: sortMethodTuple('string', (u: IUserOverview) => getName(u)),
      department: sortMethodTuple('string', nameableListToString),
      location: sortMethodTuple('string', nameableListToString),
      roles: sortMethodTuple('string', rolesToString)
    },
    defaultSortFunc: (a: IStaffTableRowValues, b) => {
      // Sort by member over invite, status, invite status, member name, and then email
      const aVal = a.invite ? { email: a.invite.email } : { ...a.member?.user };
      const bVal = b.invite ? { email: b.invite.email } : { ...b.member?.user };

      if (!aVal || !bVal) return 0;

      if (aVal.deletedAt && !bVal.deletedAt) return 1;
      if (!aVal.deletedAt && bVal.deletedAt) return -1;

      if (a.invite && getName(b.member?.user)) return 1;
      if (b.invite && getName(a.member?.user)) return -1;

      if (a.invite && b.invite) {
        const aStatus = inviteStatus(a.invite) ?? '';
        const bStatus = inviteStatus(b.invite) ?? '';

        if (aStatus === 'Expired' && bStatus !== 'Expired') return 1;
        if (bStatus === 'Expired' && aStatus !== 'Expired') return -1;

        const statusCompare = aStatus.localeCompare(bStatus);
        if (statusCompare) return statusCompare;
      }

      const aName = getName(aVal);
      const bName = getName(bVal);
      if (aName && !bName) return -1;
      if (!aName && bName) return 1;
      const nameCompare = aName.localeCompare(bName);
      if (nameCompare) return nameCompare;

      if (!aVal.email && bVal.email) return 1;
      if (!bVal.email && aVal.email) return -1;
      return (aVal.email ?? '').localeCompare(bVal.email ?? '');
    }
  });

  const sorted = useMemo(() => staff.sort(sortFunc), [sortFunc, staff]);

  return (
    <>
      <TableWithActions {...rest} {...restSort} bordered selectable loading={loading} headers={headers} values={sorted}>
        {children}
      </TableWithActions>
      <RIDialog open={inviteStaff} setOpen={setInviteStaff}>
        <SendInvitesContainer close={() => setInviteStaff(false)} members={members} />
      </RIDialog>
    </>
  );
};
